import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { getAttributeSuggestions } from "../utils/attributeSuggestions";

// Utility to convert a string to camelCase
function toCamelCase(str) {
  return str
    .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
    .replace(/^(.)/, (m) => m.toLowerCase());
}

const FIELD_TYPES = [
  "input",
  "datetime",
  "currency",
  "textarea",
  "checkbox",
  "checkboxWithOther",
  "radio",
  "radioWithOther",
  "decision",
  "decisionWithOther",
  "inlineInput",
];

/** Types that use a single primary metadata.pdfLocation (not option-level placement). */
const OPTION_LIKE_FIELD_TYPES = new Set([
  "checkbox",
  "checkboxWithOther",
  "radio",
  "radioWithOther",
  "decision",
  "decisionWithOther",
  "inlineInput",
]);

function fieldHasPrimaryPdfPlacement(field) {
  const p = field?.metadata?.pdfLocation;
  if (!p || typeof p !== "object") return false;
  return p.page != null;
}

function isFieldLinkableForPrimaryPdfAssign(field) {
  if (!field?.id || !field.attributeName) return false;
  if (OPTION_LIKE_FIELD_TYPES.has(field.type)) return false;
  return ["input", "datetime", "currency", "textarea"].includes(field.type);
}

const CreateFieldDialog = ({
  open,
  onClose,
  onCreate,
  onAssignExisting = null,
  sections = [],
  fields = [],
  initialData = null,
  mode = "json",
  variant = "drawer",
  isPurchaseContract = false,
  defaultToAssignTab = false,
}) => {
  const [newField, setNewField] = useState({
    label: "",
    type: "input",
    attributeName: "",
    placeholder: "",
    metadata: null,
    readOnly: false,
    customTemplateDoNotCopy: false,
    fieldNotes: "",
    validation: {},
    dealSummary: undefined,
    formSummary: undefined,
    formUpload: false,
    offerCompare: false,
    annotationMultiline: 0,
    isOverflow: null,
  });
  const [newFieldRules, setNewFieldRules] = useState({
    notes: "",
    sectionNumber: "",
    sectionName: "",
    sectionDescription: "",
    sectionNotes: "",
  });
  const [newFieldOptions, setNewFieldOptions] = useState([]);
  const [newFieldOther, setNewFieldOther] = useState({
    checkbox: { text: "Other", value: "other" },
    input: {
      attributeName: "",
      placeholder: "Please specify...",
      validation: {},
    },
  });
  const [attributeSuggestions, setAttributeSuggestions] = useState([]);
  const [drawerHeight, setDrawerHeight] = useState(null);
  const resizeStartRef = useRef(null);

  // Validation state
  const [attributeError, setAttributeError] = useState(null);
  const [otherInputError, setOtherInputError] = useState(null);
  const [optionErrors, setOptionErrors] = useState({}); // Map of index -> error string

  const hasPendingPdfRectangle = Boolean(initialData?.metadata?.pdfLocation);
  const [pdfPanelMode, setPdfPanelMode] = useState("create"); // "create" | "assign"
  const [assignTargetField, setAssignTargetField] = useState(null);
  const [allowReplacePdfPlacement, setAllowReplacePdfPlacement] = useState(false);

  const assignCandidateFields = useMemo(() => {
    return (fields || []).filter(
      (f) =>
        isFieldLinkableForPrimaryPdfAssign(f) &&
        (!fieldHasPrimaryPdfPlacement(f) || allowReplacePdfPlacement),
    );
  }, [fields, allowReplacePdfPlacement]);

  const openTransitionRef = useRef(false);
  useEffect(() => {
    if (open && !openTransitionRef.current) {
      const hasPdf = Boolean(initialData?.metadata?.pdfLocation);
      if (hasPdf && defaultToAssignTab && onAssignExisting) {
        setPdfPanelMode("assign");
      } else {
        setPdfPanelMode("create");
      }
      setAssignTargetField(null);
      setAllowReplacePdfPlacement(false);
    }
    openTransitionRef.current = open;
  }, [open, defaultToAssignTab, onAssignExisting]);

  useEffect(() => {
    if (open) {
      // ... reset logic remains ...
      if (initialData) {
        setNewField({
          label: "",
          type: "input",
          attributeName: "",
          placeholder: "",
          metadata: initialData.metadata || null,
          validation: {},
          dealSummary: undefined,
          formSummary: undefined,
          formUpload: false,
          offerCompare: false,
          annotationMultiline: 0,
          isOverflow: null,
          ...initialData,
        });
      } else {
        setNewField({
          label: "",
          type: "input",
          attributeName: "",
          placeholder: "",
          metadata: null,
          readOnly: false,
          customTemplateDoNotCopy: false,
          fieldNotes: "",
          validation: {},
          dealSummary: undefined,
          formSummary: undefined,
          formUpload: false,
          offerCompare: false,
          annotationMultiline: 0,
          isOverflow: null,
        });
      }
      setNewFieldOptions([]);
      setNewFieldOther({
        checkbox: { text: "Other", value: "other" },
        input: {
          attributeName: "",
          placeholder: "Please specify...",
          validation: {},
        },
      });
      setNewFieldRules({
        notes: "",
        sectionNumber: "",
        sectionName: "",
        sectionDescription: "",
        sectionNotes: "",
      });
      setAttributeError(null);
    }
  }, [open, initialData]);

  useEffect(() => {
    let isActive = true;
    getAttributeSuggestions().then((suggestions) => {
      if (isActive) {
        setAttributeSuggestions(suggestions);
      }
    });
    return () => {
      isActive = false;
    };
  }, []);

  // Validation Effect
  useEffect(() => {
    const reserved = new Set();

    // Collect existing identifiers from Fields
    fields.forEach((f) => {
      if (f.attributeName) reserved.add(f.attributeName.toLowerCase());
      // Inline inputs
      if (f.metadata?.content?.[0]?.attributeName) {
        reserved.add(f.metadata.content[0].attributeName.toLowerCase());
      }
      // Other inputs
      if (f.metadata?.other?.input?.attributeName) {
        reserved.add(f.metadata.other.input.attributeName.toLowerCase());
      }
      // Option values (if strictly enforcing option values must be unique from attributes)
      if (f.metadata?.options) {
        f.metadata.options.forEach((o) => {
          if (o.value) reserved.add(o.value.toLowerCase());
        });
      }
    });

    // Collect existing identifiers from Sections
    sections.forEach((s) => {
      if (s.attributeName) reserved.add(s.attributeName.toLowerCase());
    });

    // Helper to check uniqueness
    // For the current form being built, we need to check self-consistency too
    // but we'll do that by checking against the 'reserved' set AND other parts of current form

    const currentAttributeName = (newField.attributeName || "").trim().toLowerCase();
    const currentOtherInputName = (newFieldOther.input?.attributeName || "").trim().toLowerCase();
    const currentOptionValues = newFieldOptions
      .map((o) => (o.value || "").toLowerCase())
      .filter((v) => v);

    // 1. Validate Main Attribute Name
    if (currentAttributeName) {
      let error = null;
      if (reserved.has(currentAttributeName))
        error = "Attribute name must be unique (exists in form)";
      // Check against self-components
      if (currentAttributeName === currentOtherInputName)
        error = "Cannot match 'Other' input name";
      if (currentOptionValues.includes(currentAttributeName))
        error = "Cannot match an option value";

      setAttributeError(error);
    } else {
      setAttributeError(null);
    }

    // 2. Validate Other Input Attribute Name
    if (currentOtherInputName) {
      let error = null;
      if (reserved.has(currentOtherInputName))
        error = "Attribute name must be unique (exists in form)";
      if (currentOtherInputName === currentAttributeName)
        error = "Cannot match field attribute name";
      if (currentOptionValues.includes(currentOtherInputName))
        error = "Cannot match an option value";

      setOtherInputError(error);
    } else {
      setOtherInputError(null);
    }

    // 3. Validate Options
    const newOptionErrors = {};
    newFieldOptions.forEach((opt, idx) => {
      const val = (opt.value || "").toLowerCase();
      if (!val) return;

      if (reserved.has(val)) {
        newOptionErrors[idx] = "Value must be unique (exists in form)";
      }
      // Check against current field attributes
      if (val === currentAttributeName)
        newOptionErrors[idx] = "Cannot match field attribute name";
      if (val === currentOtherInputName)
        newOptionErrors[idx] = "Cannot match 'Other' input name";

      // Check duplicates within options
      const duplicateCount = currentOptionValues.filter(
        (v) => v === val,
      ).length;
      if (duplicateCount > 1) {
        newOptionErrors[idx] = "Duplicate option value";
      }
    });
    setOptionErrors(newOptionErrors);
  }, [
    newField.attributeName,
    newFieldOther.input.attributeName,
    newFieldOptions,
    fields,
    sections,
  ]);

  const handleCreateField = () => {
    if (mode !== "annotator" && attributeError) return;
    // ... rest of handleCreateField
    const fieldId = `field-${Date.now()}`;
    const attributeName =
      newField.isOverflow != null
        ? (newField.attributeName || newField.label || "").trim() || `field${Date.now()}`
        : mode === "annotator"
          ? (newField.attributeName || newField.label || "").trim() || `field${Date.now()}`
          : (newField.attributeName || "").trim();
        const createdField = {
      id: fieldId,
      ...newField,
      placeholder:
        newField.placeholder ||
        (newField.type === "textarea" ? "details..." : null),
      // Default properties
      constraint: null,
      constraintId: null,
      info: null,
      hint: null,
      eventTrigger: null,
      value: null,
      validation:
        newField.type === "currency"
          ? {
              min: null,
              max: null,
              minLength: null,
              maxLength: null,
              patterns: ["[0-9.,$]{1,15}"],
              required: newField.validation?.required ? true : null,
            }
          : {
              min: null,
              max: null,
              minLength: null,
              maxLength: null,
              patterns: null,
              required: newField.validation?.required ? true : null,
            },
      validationErrorMessage: newField.validation?.required ? "Required" : null,
      attributeName,
      fieldNotes: newField.fieldNotes?.trim() || "",
      rules: {
        notes: newFieldRules.notes?.trim() || "",
        sectionNumber: newFieldRules.sectionNumber?.trim() || "",
        sectionName: newFieldRules.sectionName?.trim() || "",
        sectionDescription: newFieldRules.sectionDescription?.trim() || "",
        sectionNotes: newFieldRules.sectionNotes?.trim() || "",
      },
            dealSummary: newField.dealSummary,
            formSummary: newField.formSummary,
      metadata:
        newField.type === "inlineInput"
          ? {
              content: [
                {
                  attributeName,
                  placeholder: newField.placeholder || "",
                  postLabel: "",
                  preLabel: "",
                  validation: {
                    min: null,
                    max: null,
                    minLength: null,
                    maxLength: null,
                    patterns: null,
                    required: null,
                  },
                  validationErrorMessage: null,
                  value: null,
                  width: "100px",
                },
              ],
            }
            : [
                "checkbox",
                "checkboxWithOther",
                "radio",
                "radioWithOther",
                "decision",
                "decisionWithOther",
              ].includes(newField.type)
            ? {
                options: newFieldOptions
                  .filter((opt) => opt.value || opt.text) // Filter out empty options
                  .map((opt) => {
                    const cleanOpt = { ...opt };
                    if (newField.type !== "decision" && newField.type !== "decisionWithOther") {
                      delete cleanOpt.goTo;
                    }
                    if (
                      mode === "annotator" &&
                      cleanOpt.text &&
                      !cleanOpt.value
                    ) {
                      cleanOpt.value = toCamelCase(cleanOpt.text);
                    }
                    return cleanOpt;
                  }),
                ...(["checkboxWithOther", "radioWithOther"].includes(
                  newField.type,
                ) && {
                  other: {
                    [newField.type === "radioWithOther" ? "radio" : "checkbox"]:
                      {
                        text:
                          newFieldOther.checkbox?.text ||
                          newFieldOther.radio?.text ||
                          "Other",
                        value:
                          newFieldOther.checkbox?.value ||
                          newFieldOther.radio?.value ||
                          "other",
                      },
                    input: {
                      ...newFieldOther.input,
                      attributeName:
                        (newFieldOther.checkbox?.value ||
                          newFieldOther.radio?.value ||
                          "other") + "Input",
                    },
                  },
                }),
                ...(newField.type === "decisionWithOther" && {
                  other: {
                    decisionValue: newFieldOther.decisionValue ?? "other",
                    input: { ...newFieldOther.input },
                  },
                }),
              }
            : newField.type === "datetime"
              ? {
                  ...newField.metadata, // Preserve existing metadata (e.g. pdfLocation)
                  fieldData: {
                    format:
                      newField.metadata?.fieldData?.format || "01/02/2006",
                    ...newField.metadata?.fieldData,
                  },
                }
              : newField.type === "currency"
                                ? { format: "currency" }
                                : newField.metadata, // Preserve metadata (e.g. pdfLocation)
      sectionId: null,
      readOnly: newField.readOnly || false,
      customTemplateDoNotCopy: newField.customTemplateDoNotCopy || false,
      ...(newField.formUpload ? { formUpload: true } : {}),
      ...(newField.offerCompare ? { offerCompare: true } : {}),
      annotationMultiline: newField.annotationMultiline ?? 0,
      isOverflow: newField.isOverflow ?? null,
    };

        const metadata = createdField.metadata ? { ...createdField.metadata } : {};
        if (newField.formUpload) {
            metadata.formUploadData = attributeName;
        } else {
            delete metadata.formUploadData;
        }
        delete metadata.offerCompareData;
        createdField.metadata = Object.keys(metadata).length ? metadata : createdField.metadata;

    onCreate(createdField);
    onClose();
  };

  const handleEnterCreate = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    handleCreateField();
  };

  const handleAddNewOption = () => {
    const optionTypes = ["checkbox", "checkboxWithOther", "radio", "radioWithOther", "decision", "decisionWithOther"];
    const isOptionType = optionTypes.includes(newField.type);
    if (mode === "annotator" && isOptionType) {
      const prefix =
        newField.type === "decision" || newField.type === "decisionWithOther"
          ? "decision"
          : newField.type.startsWith("radio")
            ? "radio"
            : "checkbox";
      const n = newFieldOptions.length + 1;
      const defaultLabel = `${prefix}${n}`;
      const defaultValue = toCamelCase(defaultLabel) || defaultLabel;
      setNewFieldOptions([
        ...newFieldOptions,
        { value: defaultValue, text: defaultLabel, goTo: null },
      ]);
    } else {
      setNewFieldOptions([
        ...newFieldOptions,
        { value: "", text: "", goTo: null },
      ]);
    }
  };

  const handleRemoveNewOption = (index) => {
    setNewFieldOptions(newFieldOptions.filter((_, i) => i !== index));
  };

  const handleNewOptionChange = (index, field, value) => {
    const newOptions = [...newFieldOptions];
    const nextOption = { ...newOptions[index], [field]: value };
    if (mode === "annotator" && field === "text") {
      const nextValue = toCamelCase(value || "");
      if (
        !nextOption.value ||
        nextOption.value === toCamelCase(newOptions[index]?.text || "")
      ) {
        nextOption.value = nextValue;
      }
    }
    newOptions[index] = nextOption;
    setNewFieldOptions(newOptions);
  };

  const getAvailableSections = () => {
    return sections.map((section) => ({
      value: section.attributeName,
      label: section.title,
    }));
  };

  const renderOptionFields = (option, index) => {
    return (
      <Box
        key={index}
        sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
      >
        {mode !== "annotator" && (
          <TextField
            label="Value"
            value={option.value}
            onChange={(e) =>
              handleNewOptionChange(index, "value", e.target.value)
            }
            size="small"
            error={!!optionErrors[index]}
            helperText={optionErrors[index]}
            sx={{ minWidth: 120 }}
          />
        )}
        <TextField
          label={mode === "annotator" ? "Label" : "Text"}
          value={option.text}
          onChange={(e) => handleNewOptionChange(index, "text", e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        />
        {mode !== "annotator" && (newField.type === "decision" || newField.type === "decisionWithOther") && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Go To Section</InputLabel>
            <Select
              value={option.goTo || ""}
              onChange={(e) =>
                handleNewOptionChange(index, "goTo", e.target.value)
              }
              label="Go To Section"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {getAvailableSections().map((section) => (
                <MenuItem key={section.value} value={section.value}>
                  {section.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <IconButton
          onClick={() => handleRemoveNewOption(index)}
          color="error"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const container = document.getElementById("pdf-viewer-container");

  useEffect(() => {
    if (!open) {
      setDrawerHeight(null);
    }
  }, [open]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!resizeStartRef.current) return;
      const { startY, startHeight, minHeight, maxHeight } =
        resizeStartRef.current;
      const nextHeight = Math.min(
        Math.max(startHeight - (event.clientY - startY), minHeight),
        maxHeight,
      );
      setDrawerHeight(nextHeight);
    };

    const handleMouseUp = () => {
      resizeStartRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const dialogContent = (
    <>
      {variant === "drawer" && (
        <Box
          onMouseDown={(event) => {
            resizeStartRef.current = {
              startY: event.clientY,
              startHeight:
                event.currentTarget.parentElement?.getBoundingClientRect()
                  .height || 0,
              minHeight: 220,
              maxHeight: window.innerHeight * 0.8,
            };
            event.preventDefault();
          }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 0.5,
            cursor: "ns-resize",
            color: "text.disabled",
            userSelect: "none",
          }}
        >
          <DragHandleIcon fontSize="small" />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {hasPendingPdfRectangle && pdfPanelMode === "assign"
            ? "Assign PDF to existing field"
            : "Create New Field"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          px: 2,
          py: 1.5,
        }}
      >
        <Stack spacing={1.5}>
          {hasPendingPdfRectangle && onAssignExisting && (
            <ToggleButtonGroup
              exclusive
              size="small"
              fullWidth
              value={pdfPanelMode}
              onChange={(_, next) => {
                if (next != null) setPdfPanelMode(next);
              }}
              sx={{ mb: 0.5 }}
            >
              <ToggleButton value="create" sx={{ textTransform: "none" }}>
                Create new field
              </ToggleButton>
              <ToggleButton value="assign" sx={{ textTransform: "none" }}>
                Assign to existing field
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {pdfPanelMode === "assign" && hasPendingPdfRectangle && onAssignExisting ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Choose a field from your form JSON. The drawn rectangle becomes its primary{" "}
                <code>metadata.pdfLocation</code> (option-style fields use a different flow).
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allowReplacePdfPlacement}
                    onChange={(e) => {
                      setAllowReplacePdfPlacement(e.target.checked);
                      setAssignTargetField(null);
                    }}
                    size="small"
                  />
                }
                label="Show fields that already have a PDF placement (replace)"
              />
              <Autocomplete
                options={assignCandidateFields}
                value={assignTargetField}
                onChange={(_, value) => setAssignTargetField(value)}
                getOptionLabel={(f) =>
                  f
                    ? `${f.label || f.attributeName || f.id} (${f.attributeName}) · ${f.type}`
                    : ""
                }
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Existing field"
                    placeholder="Search by label or attribute name"
                    size="small"
                  />
                )}
                filterOptions={(opts, state) => {
                  const q = (state.inputValue || "").trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((f) => {
                    const label = (f.label || "").toLowerCase();
                    const attr = (f.attributeName || "").toLowerCase();
                    return label.includes(q) || attr.includes(q);
                  });
                }}
              />
              {assignCandidateFields.length === 0 && (
                <Alert severity="info">
                  No matching fields. Import JSON fields are usually <strong>input</strong>,{" "}
                  <strong>textarea</strong>, <strong>currency</strong>, or <strong>datetime</strong>. Enable
                  &quot;replace&quot; to pick fields that already have a placement.
                </Alert>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  pt: 1,
                }}
              >
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={!assignTargetField}
                  onClick={() => {
                    if (assignTargetField && onAssignExisting) {
                      onAssignExisting(assignTargetField.id);
                    }
                  }}
                >
                  Assign rectangle
                </Button>
              </Box>
            </>
          ) : (
            <>
          <TextField
            select
            label="Field Type"
            value={mode === "annotator" && !["input", "checkbox"].includes(newField.type) ? "input" : newField.type}
            size="small"
            onChange={(e) => {
              const type = e.target.value;
              const isOptionType = [
                "checkbox",
                "checkboxWithOther",
                "radio",
                "radioWithOther",
                "decision",
              ].includes(type);

              setNewField({
                ...newField,
                type,
                metadata: isOptionType ? { options: [] } : newField.metadata, // Preserve existing metadata if not option type
              });

              if (!isOptionType) {
                setNewFieldOptions([]);
              }
            }}
            fullWidth
            required
          >
            {(mode === "annotator" ? ["input", "checkbox"] : FIELD_TYPES).map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          {mode !== "annotator" && (
            <TextField
              label="Label"
              value={newField.label}
              onChange={(e) =>
                setNewField({ ...newField, label: e.target.value })
              }
              onKeyDown={handleEnterCreate}
              fullWidth
              required
              size="small"
            />
          )}
          <TextField
            label="Field Notes"
            placeholder="Seller Only, Read Only, etc"
            value={newField.fieldNotes}
            onChange={(e) =>
              setNewField({ ...newField, fieldNotes: e.target.value })
            }
            onKeyDown={handleEnterCreate}
            fullWidth
            size="small"
          />
          {mode !== "annotator" && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                Annotation
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                <TextField
                  label="Annotation multi-line (lines)"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={newField.annotationMultiline ?? 0}
                  onChange={(e) => {
                    const v = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                    setNewField({
                      ...newField,
                      annotationMultiline: isNaN(v) ? 0 : Math.max(0, v),
                    });
                  }}
                  helperText="0 = none; e.g. 3 for overflow box, 11 for wrap-in-box"
                  sx={{ minWidth: 140 }}
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="create-field-is-overflow-label">Is Overflow</InputLabel>
                  <Select
                    labelId="create-field-is-overflow-label"
                    label="Is Overflow"
                    value={newField.isOverflow ?? ""}
                    onChange={(e) =>
                      setNewField({
                        ...newField,
                        isOverflow: e.target.value || null,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {fields
                      .filter((f) => !f.isOverflow && f.attributeName)
                      .map((f) => (
                        <MenuItem key={f.id} value={f.attributeName}>
                          {f.attributeName}
                        </MenuItem>
                      ))}
                  </Select>
                  {newField.isOverflow && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      This box will be stored under the target field&apos;s metadata.overflow.
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </>
          )}
          {mode !== "annotator" && (
            <>
              <Autocomplete
                freeSolo
                options={attributeSuggestions}
                value={newField.attributeName || ""}
                onChange={(_, value) =>
                  setNewField({ ...newField, attributeName: value || "" })
                }
                onInputChange={(_, value) =>
                  setNewField({ ...newField, attributeName: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Attribute Name"
                    error={!!attributeError}
                    helperText={
                      attributeError ||
                      "Enter the attribute name (casing is preserved on save)"
                    }
                    size="small"
                  />
                )}
              />
              <TextField
                label="Placeholder"
                value={newField.placeholder}
                onChange={(e) =>
                  setNewField({ ...newField, placeholder: e.target.value })
                }
                fullWidth
                size="small"
              />
            </>
          )}
          {mode !== "annotator" && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newField.validation?.required === true}
                    onChange={(e) => {
                      const isRequired = e.target.checked;
                      setNewField({
                        ...newField,
                        validation: {
                          ...newField.validation,
                          required: isRequired ? true : null,
                        },
                      });
                      if (!isRequired) {
                        setNewFieldRules((prev) => ({
                          ...prev,
                          notes: "",
                        }));
                      }
                    }}
                  />
                }
                label="Required Field"
              />
              {newField.validation?.required === true && (
                <TextField
                  label="Notes"
                  placeholder="Why is this field Required?"
                  value={newFieldRules.notes}
                  onChange={(e) =>
                    setNewFieldRules((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                />
              )}
            </Box>
          )}

          {[
            "checkbox",
            "checkboxWithOther",
            "radio",
            "radioWithOther",
            "decision",
          ].includes(newField.type) && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Options
              </Typography>
              <Stack spacing={2}>
                {newFieldOptions.map((option, index) =>
                  renderOptionFields(option, index),
                )}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddNewOption}
                  variant="outlined"
                >
                  Add Option
                </Button>
              </Stack>
            </Box>
          )}

          {/* Other Option Configuration */}
          {mode !== "annotator" &&
            ["checkboxWithOther", "radioWithOther"].includes(newField.type) && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" gutterBottom>
                  "Other" Option Configuration
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  <TextField
                    label="Option Label"
                    value={
                      newField.type === "radioWithOther"
                        ? newFieldOther.radio?.text || ""
                        : newFieldOther.checkbox?.text || ""
                    }
                    onChange={(e) => {
                      const key =
                        newField.type === "radioWithOther"
                          ? "radio"
                          : "checkbox";
                      setNewFieldOther({
                        ...newFieldOther,
                        [key]: { ...newFieldOther[key], text: e.target.value },
                      });
                    }}
                    size="small"
                    sx={{ flexGrow: 1 }}
                    helperText="Label for the checkbox/radio"
                  />
                  <TextField
                    label="Option Value"
                    value={
                      newField.type === "radioWithOther"
                        ? newFieldOther.radio?.value || ""
                        : newFieldOther.checkbox?.value || ""
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const expectedInputName = newValue
                        ? `${newValue}Input`
                        : "";
                      const key =
                        newField.type === "radioWithOther"
                          ? "radio"
                          : "checkbox";
                      setNewFieldOther({
                        ...newFieldOther,
                        [key]: { ...newFieldOther[key], value: newValue },
                        input: {
                          ...newFieldOther.input,
                          attributeName: expectedInputName,
                        },
                      });
                    }}
                    size="small"
                    sx={{ flexGrow: 1 }}
                    helperText="Saved value when selected (Input attribute name will be: {value}Input)"
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {/* Input Attribute Name is auto-generated: {value}Input - Hidden but kept for programmatic access */}
                  <TextField
                    label="Input Attribute Name"
                    value={newFieldOther.input?.attributeName || ""}
                    onChange={(e) =>
                      setNewFieldOther({
                        ...newFieldOther,
                        input: {
                          ...newFieldOther.input,
                          attributeName: e.target.value,
                        },
                      })
                    }
                    error={!!otherInputError}
                    helperText={
                      otherInputError || "Attribute name for the text input"
                    }
                    size="small"
                    sx={{ flexGrow: 1, display: "none" }}
                  />
                  <TextField
                    label="Input Placeholder"
                    value={newFieldOther.input?.placeholder || ""}
                    onChange={(e) =>
                      setNewFieldOther({
                        ...newFieldOther,
                        input: {
                          ...newFieldOther.input,
                          placeholder: e.target.value,
                        },
                      })
                    }
                    size="small"
                    sx={{ flexGrow: 1 }}
                    helperText="Placeholder text for the input"
                  />
                </Box>
              </Box>
            )}

          {/* Additional Field Properties */}
          {mode !== "annotator" && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Additional Properties
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newField.readOnly || false}
                      onChange={(e) =>
                        setNewField({ ...newField, readOnly: e.target.checked })
                      }
                    />
                  }
                  label="Read Only (pre-filled from deal data)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newField.customTemplateDoNotCopy || false}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          customTemplateDoNotCopy: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Custom Template (Do Not Copy)"
                />
              </Stack>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              pt: 1,
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleCreateField}
              variant="contained"
              disabled={
                (mode !== "annotator" && !newField.label) ||
                !newField.type ||
                (mode !== "annotator" && !newField.attributeName) ||
                (mode !== "annotator" && !!attributeError) ||
                (mode !== "annotator" && !!otherInputError) ||
                (mode !== "annotator" &&
                  Object.keys(optionErrors).length > 0) ||
                (["checkbox", "radio", "decision"].includes(newField.type) &&
                  newFieldOptions.length === 0)
              }
            >
              Create
            </Button>
          </Box>
            </>
          )}
        </Stack>
      </Box>
    </>
  );

  if (variant === "panel") {
    if (!open) return null;
    return (
      <Paper
        elevation={24}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {dialogContent}
      </Paper>
    );
  }

  return open && container
    ? ReactDOM.createPortal(
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            width: "100%",
            height: drawerHeight ? `${drawerHeight}px` : "auto",
            minHeight: "220px",
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          {dialogContent}
        </Box>,
        container,
      )
    : null;
};

export default CreateFieldDialog;
